/**
 * Detalhe da requisição (ADR-0069): drawer `SlideOverPanel` e página
 * (`DetalhePagina`) compartilham o mesmo corpo (`DetalheCorpo`): stepper das
 * situações, meta (prazo, comprador, parada há, valor cotado), itens com
 * controle segmentado, cotação comparativa por item, histórico e
 * comentários com @menção. Ações no rodapé; motivos em modal, nunca prompt.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FiAward, FiCheck, FiDollarSign, FiEdit2, FiExternalLink, FiEye, FiPaperclip, FiPlus, FiSend, FiTrash2,
} from "react-icons/fi";
import styled from "styled-components";
import { Link } from "react-router-dom";

import SlideOverPanel from "../../../components/SlideOverPanel";
import { PHARUCUT_URL } from "../../../config/produtos";
import { useAuth } from "../../../context/AuthContext";
import {
  SITUACAO_ITEM_LABEL, SITUACOES_ITEM_REQ, TIPO_TERCEIRO_LABEL,
  atribuirComprador, atualizarRequisicao, comentarRequisicao, escolherCotacaoVencedora, eventosDaRequisicao, listarComentarios,
  removerCotacao, urlArquivoAnexoRqc,
} from "../../../services/compras";
import { STEPPER, SITUACAO_TONE, TIPO_EVENTO_TONE, paradaHa } from "../../../services/requisicoesCompraUi";
import { getToneTokens } from "../../../styles/shared";
import { formatarDataLocal, formatarReaisBR } from "../../../utils/dinheiro";
import { fmtQt } from "../../RevisorFerragens/lib/documento";
import { Carregando } from "../_shared/Estados";
import { Mono, Tag } from "../_shared/quadro/styles";
import CotacaoConjunta from "./CotacaoConjunta";
import { itemCotavel } from "./regrasDaCotacao";
import ItensDaRequisicao, { nomeDoItem } from "./ItensDaRequisicao";
import VencedorEmLote from "./VencedorEmLote";
import QuadroFornecedores from "./QuadroFornecedores";
import VisualizadorDeAnexo from "./VisualizadorDeAnexo";
import { Avatar, ModalMotivo, Rotulo, Segmented, StatusPill, TagOrigem, TagUrgente } from "./ui";

// ---------------------------------------------------------------- estilos

const Stepper = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 6px;
  > div .barra { height: 4px; border-radius: 2px; background: var(--border); }
  > div .rotulo { font-size: 11px; color: var(--ink-4); margin-top: 6px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  > div[data-on="true"] .rotulo { color: var(--ink); font-weight: 600; }
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 14px 20px;
  > div > .v { font-size: 13px; font-weight: 500; display: flex; align-items: center; gap: 8px; min-width: 0; flex-wrap: wrap; }
`;

const Caixa = styled.div`
  background: var(--surface-2);
  border-radius: 12px;
  padding: 12px 14px;
  font-size: 13px;
  white-space: pre-wrap;
`;

const CotTabela = styled.div`
  border: 1px solid var(--border);
  border-radius: 12px;
  overflow: hidden;
  font-size: 12.5px;
  > .linha { display: grid; grid-template-columns: minmax(0, 1.4fr) 100px 90px 110px 90px; gap: 10px; align-items: center; padding: 8px 12px; border-bottom: 1px solid var(--border-subtle); min-width: 0; }
  > .linha:last-child { border-bottom: 0; }
  > .linha.cab { background: var(--surface-2); font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .06em; color: var(--ink-3); height: 36px; }
  > .linha[data-venc="true"] { background: var(--success-row); }
  > .linha > div { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  @media (max-width: 640px) { > .linha { grid-template-columns: minmax(0, 1fr) 90px 90px; } > .linha > .esc { display: none; } }
`;

const Duas = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
`;

const Trilho = styled.div`
  position: relative;
  padding-left: 18px;
  &::before { content: ""; position: absolute; left: 5px; top: 6px; bottom: 6px; width: 2px; background: var(--border); }
  > div { position: relative; padding: 4px 0 10px; font-size: 12.5px; }
  > div::before { content: ""; position: absolute; left: -18px; top: 9px; width: 10px; height: 10px; border-radius: 50%; background: var(--dot, var(--neutral-solid)); border: 2px solid var(--surface); box-sizing: border-box; }
  > div .q { color: var(--ink-4); font-family: var(--font-mono); font-size: 11px; }
`;

const ComentarioCard = styled.div`
  display: flex;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid var(--border-subtle);
  &:last-child { border-bottom: 0; }
  > div { min-width: 0; flex: 1; }
  .quem { font-size: 12px; display: flex; gap: 8px; align-items: baseline; }
  .quem .h { color: var(--ink-4); font-family: var(--font-mono); font-size: 11px; }
  .txt { font-size: 13px; white-space: pre-wrap; margin-top: 2px; }
  .txt .m { color: var(--accent); font-weight: 600; }
`;

const CAMPO = {
  height: 32, padding: "0 10px", borderRadius: 8, border: "1px solid var(--border)",
  background: "var(--surface)", color: "var(--ink)", font: "inherit", fontSize: 13, width: "100%", boxSizing: "border-box",
};

/**
 * Uma data do cabeçalho com o lápis ao lado (2026-10-09): clicar abre o
 * campo, mudar grava pelo PATCH e a tela relê. O backend recusa data no
 * passado e cotação depois da data em que a serra precisa — o erro chega
 * pelo toast automático do request.
 */
function DataEditavel({ valor, podeEditar, onSalvar, rotuloDoBotao, vazio = "—", corDoValor }) {
  const [editando, setEditando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  if (editando) {
    return (
      <input
        type="date"
        autoFocus
        defaultValue={valor || ""}
        min={new Date().toISOString().slice(0, 10)}
        disabled={salvando}
        aria-label={rotuloDoBotao}
        onKeyDown={(e) => { if (e.key === "Escape") setEditando(false); }}
        onChange={async (e) => {
          const nova = e.target.value;
          if (!nova || nova === valor) return;
          setSalvando(true);
          try {
            await onSalvar(nova);
            setEditando(false);
          } finally {
            setSalvando(false);
          }
        }}
        onBlur={() => { if (!salvando) setEditando(false); }}
        style={{ ...CAMPO, width: 160 }}
      />
    );
  }
  return (
    <>
      <span style={{ color: corDoValor }}>{valor ? formatarDataLocal(valor) : vazio}</span>
      {podeEditar ? (
        <button type="button" onClick={() => setEditando(true)} aria-label={rotuloDoBotao} style={{ background: "none", border: "none", color: "var(--accent)", fontSize: 12, cursor: "pointer", padding: 0 }}>
          alterar
        </button>
      ) : null}
    </>
  );
}

function dataHora(iso) {
  return iso ? new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";
}

function detalheDoItem(it) {
  if (it.eh_terceiro) return TIPO_TERCEIRO_LABEL[it.tipo_terceiro] || it.tipo_terceiro_label;
  const base = it.eh_livre
    ? [it.unidade, it.categoria_label, "não cadastrado"]
    : [it.produto_codigo, it.produto_unidade];
  return [...base, it.marca, it.modelo].filter(Boolean).join(" · ");
}

/** Quem pediu quanto numa linha somada (ADR-0074): "Joao 10 + Maria 5". */
function PedidosDoItem({ pedidos }) {
  if (!pedidos || pedidos.length === 0) return null;
  return (
    <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 4, display: "grid", gap: 2 }}>
      {pedidos.map((p) => (
        <div key={p.id}>
          <strong style={{ fontWeight: 600, color: "var(--ink-2)" }}>{p.solicitante_nome || "—"}</strong>
          {` ×${fmtQt(p.quantidade)} · ${dataHora(p.created_at)}`}
          {p.data_necessaria ? ` · precisa até ${formatarDataLocal(p.data_necessaria)}` : ""}
          {p.prioridade === "URGENTE" ? " · urgente" : ""}
          {[p.marca, p.modelo].filter(Boolean).length ? ` · ${[p.marca, p.modelo].filter(Boolean).join(" ")}` : ""}
          {p.referencia ? ` · ${p.referencia}` : ""}
          {p.especificacao ? <div style={{ color: "var(--ink-4)" }}>{p.especificacao}</div> : null}
          {p.observacao ? <div style={{ color: "var(--ink-4)" }}>{p.observacao}</div> : null}
        </div>
      ))}
    </div>
  );
}

const SITUACAO_LINHA_CUT = {
  COMPRAR: { rotulo: "Comprar", tone: "warning" },
  EM_ESTOQUE: { rotulo: "Em estoque", tone: "success" },
  SEM_CADASTRO: { rotulo: "Sem cadastro", tone: "danger" },
  NAO_CONVERTE: { rotulo: "Unidade não converte", tone: "danger" },
  NAO_SE_COMPRA: { rotulo: "Não se compra (retalho)", tone: "neutral" },
};

/** O plano do PharuCut, linha a linha: Consome / Em estoque / Comprar (D1/D8/D9).
 *  A falta e recalculada pelo servidor a cada abertura; "era X" marca o que
 *  mudou desde o envio. */
function PlanoDoCut({ linhas }) {
  const q = (v) => (v == null ? "—" : fmtQt(v));
  return (
    <div>
      <Rotulo>Plano do PharuCut ({linhas.length} materiais)</Rotulo>
      <div style={{ overflowX: "hidden" }}>
        <table style={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ color: "var(--ink-4)", fontSize: 11, fontFamily: "var(--font-mono)", textAlign: "left" }}>
              <th style={{ padding: "4px 6px", width: "44%" }}>MATERIAL</th>
              <th style={{ padding: "4px 6px", textAlign: "right" }}>CONSOME</th>
              <th style={{ padding: "4px 6px", textAlign: "right" }}>EM ESTOQUE</th>
              <th style={{ padding: "4px 6px", textAlign: "right" }}>COMPRAR</th>
              <th style={{ padding: "4px 6px", width: "17%" }}>SITUAÇÃO</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map((l) => {
              const s = SITUACAO_LINHA_CUT[l.situacao] || { rotulo: l.situacao, tone: "neutral" };
              const unidade = l.produto_unidade || l.unidade;
              const mudou = l.situacao === "COMPRAR" && l.qtd_falta != null && Number(l.falta_agora) !== Number(l.qtd_falta);
              return (
                <tr key={l.id} style={{ borderTop: "1px solid var(--border)" }}>
                  {/* Uma linha por material: nome e código lado a lado (o código inteiro fica no title). */}
                  <td style={{ padding: "3px 6px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={`${l.codigo} — ${l.produto_nome || "sem cadastro"}${l.motivo ? ` · ${l.motivo}` : ""}`}>
                    <span style={{ fontWeight: 600 }}>{l.produto_nome || l.codigo}</span>
                    {l.produto_nome ? <span style={{ fontSize: 11, color: "var(--ink-4)", fontFamily: "var(--font-mono)" }}> · {l.codigo}</span> : null}
                  </td>
                  <td style={{ padding: "3px 6px", textAlign: "right" }}><Mono $size={13}>{q(l.consumo_convertido ?? l.qtd_consumo)} {unidade}</Mono></td>
                  <td style={{ padding: "3px 6px", textAlign: "right" }}><Mono $size={13}>{l.disponivel != null ? `${q(l.disponivel)} ${unidade}` : "—"}</Mono></td>
                  <td style={{ padding: "3px 6px", textAlign: "right" }} title={mudou ? `Era ${q(l.qtd_falta)} ${unidade} no envio` : undefined}>
                    <Mono $size={13}>{l.situacao === "COMPRAR" ? `${q(l.falta_agora)} ${unidade}` : "—"}</Mono>
                    {mudou ? <span style={{ fontSize: 11, color: "var(--ink-4)" }}> (era {q(l.qtd_falta)})</span> : null}
                  </td>
                  <td style={{ padding: "3px 6px" }}><Tag $tone={s.tone} $h={18} title={l.motivo || undefined}>{s.rotulo}</Tag></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** O formulario como o escritorio enviou: justificativa, urgencia e anexos. */
function RqcsDaRequisicao({ req, rqcs }) {
  const [aberto, setAberto] = useState(null);
  if (!rqcs || rqcs.length === 0) return null;
  return (
    <div>
      <Rotulo>Pedidos do escritório (RQC)</Rotulo>
      <div style={{ display: "grid", gap: 8 }}>
        {rqcs.map((r) => (
          <Caixa key={r.id} style={{ margin: 0 }}>
            <div>
              <strong>{r.solicitante_nome || "—"}</strong>
              {r.setor ? ` (${r.setor})` : ""} · {dataHora(r.created_at)} · {r.motivo_label}
              {r.data_necessaria ? ` · precisa até ${formatarDataLocal(r.data_necessaria)}` : ""}
            </div>
            {r.prioridade === "URGENTE" ? (
              <div style={{ color: "var(--danger-text)" }}>Urgente — {r.motivo_urgencia}</div>
            ) : null}
            <div>{r.justificativa}</div>
            {(r.anexos || []).length ? (
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 4 }}>
                {r.anexos.map((a) => (
                  <button
                    key={a.id} type="button" title={`Ver ${a.nome}`}
                    onClick={() => setAberto({ ...a, download_url: urlArquivoAnexoRqc(req.id, a.id) })}
                    style={{ ...{ background: "none", border: "none", padding: 0, cursor: "pointer", color: "var(--accent-text, var(--accent))", font: "inherit", display: "inline-flex", gap: 4, alignItems: "center", minWidth: 0 }, fontSize: 12, gap: 5 }}
                  >
                    <FiEye size={12} /> {a.nome}
                  </button>
                ))}
              </div>
            ) : null}
          </Caixa>
        ))}
      </div>
      {aberto ? (
        <VisualizadorDeAnexo
          anexo={aberto}
          cabecalho={{ titulo: `${req.numero} · RQC`, detalhe: aberto.nome }}
          onFechar={() => setAberto(null)}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- cotação

function CotacaoDoItem({ req, item, podeGerir, onMudou, onGerarOc, onCotar }) {
  const { request } = useAuth();
  const [erro, setErro] = useState(null);
  const cotacoes = useMemo(() => item.cotacoes || [], [item.cotacoes]);

  const melhor = useMemo(() => {
    const respondidas = cotacoes.filter((c) => c.valor_unitario !== null && c.valor_unitario !== undefined);
    if (!respondidas.length) return { preco: null, prazo: null };
    const preco = respondidas.reduce((a, b) => (Number(a.total_com_frete ?? 0) <= Number(b.total_com_frete ?? 0) ? a : b));
    const comPrazo = respondidas.filter((c) => c.prazo_dias !== null && c.prazo_dias !== undefined);
    const prazo = comPrazo.length ? comPrazo.reduce((a, b) => (a.prazo_dias <= b.prazo_dias ? a : b)) : null;
    return { preco: preco.id, prazo: prazo?.id };
  }, [cotacoes]);

  async function vencedora(c) {
    try { await escolherCotacaoVencedora(request, req.id, c.id); await onMudou(); } catch (err) { setErro(err?.payload?.detalhe || err?.message); }
  }

  async function remover(c) {
    try { await removerCotacao(request, req.id, c.id); await onMudou(); } catch (err) { setErro(err?.payload?.detalhe || err?.message); }
  }

  const temVencedora = cotacoes.some((c) => c.vencedora);
  const podeGerarOc = podeGerir && temVencedora && req.situacao === "APROVADA" && !item.eh_terceiro && item.situacao !== "COMPRADO";
  const aberto = podeGerir && itemCotavel(item);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <strong style={{ fontSize: 13 }}>Cotação</strong>
        <span style={{ fontSize: 12, color: "var(--ink-3)" }}>· {nomeDoItem(item)} ×{fmtQt(item.qtd_aprovada || item.qtd_solicitada)}</span>
        <span style={{ flex: 1 }} />
        {aberto ? (
          <button type="button" className="btn btn--secondary btn--sm" onClick={() => onCotar([item])}>
            <FiPlus size={12} /> Fornecedor
          </button>
        ) : null}
        {podeGerarOc ? (
          <button type="button" className="btn btn--primary btn--sm" onClick={() => onGerarOc([item.id])}>
            <FiDollarSign size={12} /> Gerar OC com o vencedor
          </button>
        ) : null}
      </div>
      {cotacoes.length ? (
        <CotTabela>
          <div className="linha cab"><div>Fornecedor</div><div>Unit.</div><div className="esc">Prazo</div><div>Total c/ frete</div><div className="esc" /></div>
          {cotacoes.map((c) => {
            const respondeu = c.valor_unitario !== null && c.valor_unitario !== undefined;
            return (
              <div className="linha" key={c.id} data-venc={c.vencedora ? "true" : "false"}>
                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <span style={{ fontWeight: 600 }}>{c.fornecedor_nome}</span>
                  {c.vencedora ? <Tag $tone="success" $h={18}>Vencedor</Tag>
                    : c.nao_tem ? <Tag $tone="neutral" $h={18}>Não tem</Tag>
                    : !respondeu ? <span style={{ fontSize: 11, color: "var(--ink-4)" }}>Aguardando resposta{c.enviado_em ? ` · enviado ${formatarDataLocal(c.enviado_em)}` : ""}</span>
                      : melhor.preco === c.id ? <Tag $tone="success" $h={18}>Melhor preço</Tag>
                        : melhor.prazo === c.id ? <Tag $tone="info" $h={18}>Menor prazo</Tag> : null}
                </div>
                <div><Mono>{respondeu ? formatarReaisBR(c.valor_unitario) : "—"}</Mono></div>
                <div className="esc">{c.prazo_dias !== null && c.prazo_dias !== undefined ? `${c.prazo_dias} dias` : "—"}</div>
                <div><Mono style={{ fontWeight: c.vencedora ? 700 : 500 }}>{c.total_com_frete ? formatarReaisBR(c.total_com_frete) : "—"}</Mono></div>
                <div className="esc" style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                  {aberto ? (
                    <>
                      {!c.vencedora && respondeu ? (
                        <button type="button" className="btn btn--secondary btn--sm" onClick={() => vencedora(c)}>Escolher</button>
                      ) : null}
                      <button type="button" className="btn btn--secondary btn--sm" aria-label={`Editar cotação de ${c.fornecedor_nome}`} onClick={() => onCotar([item], c.fornecedor)}><FiEdit2 size={12} /></button>
                      {!c.vencedora ? (
                        <button type="button" className="btn btn--secondary btn--sm" aria-label="Remover cotação" onClick={() => remover(c)} style={{ color: "var(--danger-text)" }}><FiTrash2 size={12} /></button>
                      ) : null}
                    </>
                  ) : null}
                </div>
              </div>
            );
          })}
        </CotTabela>
      ) : (
        <div style={{ fontSize: 12, color: "var(--ink-4)" }}>Nenhum fornecedor cotado ainda.</div>
      )}
      {erro ? <div style={{ fontSize: 12, color: "var(--danger-text)" }}>{erro}</div> : null}
    </div>
  );
}

/**
 * Seleção para cotar junto (2026-10-02): todos, alguns ou nenhum — sem
 * seleção, cada item segue com o seu "Cotar".
 */
function BarraDeSelecao({ cotaveis, selecionados, onTodos, onCotar, onVencedor, onLimpar }) {
  if (!cotaveis.length) return null;
  const n = selecionados.length;
  const todos = n === cotaveis.length;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", fontSize: 13 }}>
      <label style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", minHeight: "var(--tap-min, 32px)" }}>
        <input
          type="checkbox"
          checked={todos}
          ref={(el) => { if (el) el.indeterminate = n > 0 && !todos; }}
          onChange={() => onTodos(!todos)}
          aria-label="Selecionar todos os itens a cotar"
        />
        {n ? `${n} de ${cotaveis.length} selecionados` : "Selecionar itens para cotar juntos"}
      </label>
      {n ? (
        <>
          <button type="button" className="btn btn--primary btn--sm" onClick={onCotar}>
            <FiDollarSign size={12} /> Cotar selecionados ({n})
          </button>
          <button type="button" className="btn btn--secondary btn--sm" onClick={onVencedor}>
            <FiAward size={12} /> Escolher vencedor…
          </button>
          <button type="button" className="btn btn--ghost btn--sm" onClick={onLimpar}>Limpar</button>
        </>
      ) : null}
    </div>
  );
}

function textoComMencoes(texto) {
  return texto.split(/(@[\wÀ-ÿ.]+(?:\s[A-ZÀ-Ý][\wÀ-ÿ]+)?)/g).map((parte, i) => (
    parte.startsWith("@") ? <span className="m" key={i}>{parte}</span> : <React.Fragment key={i}>{parte}</React.Fragment>
  ));
}

function Comentarios({ req, comentarios, onEnviado, pessoas }) {
  const { request } = useAuth();
  const [texto, setTexto] = useState("");
  const [mencionados, setMencionados] = useState([]);
  const [sugestao, setSugestao] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const inputRef = useRef(null);

  function aoDigitar(v) {
    setTexto(v);
    const m = /@([\wÀ-ÿ]*)$/.exec(v);
    setSugestao(m ? m[1].toLowerCase() : null);
  }

  function mencionar(p) {
    setTexto((t) => t.replace(/@([\wÀ-ÿ]*)$/, `@${p.nome.split(" ")[0]} `));
    setMencionados((m) => (m.includes(p.id) ? m : [...m, p.id]));
    setSugestao(null);
    inputRef.current?.focus();
  }

  async function enviar() {
    if (!texto.trim()) return;
    setEnviando(true);
    try {
      await comentarRequisicao(request, req.id, texto.trim(), mencionados);
      setTexto("");
      setMencionados([]);
      await onEnviado();
    } finally {
      setEnviando(false);
    }
  }

  const candidatos = sugestao !== null
    ? pessoas.filter((p) => p.nome.toLowerCase().includes(sugestao)).slice(0, 6) : [];

  return (
    <div>
      <Rotulo>Comentários ({comentarios.length})</Rotulo>
      <div style={{ border: "1px solid var(--border)", borderRadius: 12, padding: "4px 14px", marginBottom: 10, maxHeight: 280, overflowY: "auto" }}>
        {comentarios.length === 0 ? (
          <div style={{ fontSize: 12, color: "var(--ink-4)", padding: "10px 0" }}>Nenhum comentário ainda.</div>
        ) : comentarios.map((c) => (
          <ComentarioCard key={c.id}>
            <Avatar nome={c.autor_nome} size={26} />
            <div>
              <div className="quem"><strong>{c.autor_nome || "—"}</strong><span className="h">{dataHora(c.created_at)}</span></div>
              <div className="txt">{textoComMencoes(c.texto)}</div>
            </div>
          </ComentarioCard>
        ))}
      </div>
      <div style={{ position: "relative" }}>
        <textarea
          ref={inputRef}
          value={texto}
          onChange={(e) => aoDigitar(e.target.value)}
          onKeyDown={(e) => { if ((e.ctrlKey || e.metaKey) && e.key === "Enter") enviar(); }}
          placeholder="Comentar… use @ para mencionar"
          rows={2}
          style={{ ...CAMPO, height: "auto", padding: "8px 44px 8px 10px", resize: "none", display: "block" }}
        />
        <button
          type="button"
          className="btn btn--primary"
          onClick={enviar}
          disabled={enviando || !texto.trim()}
          aria-label="Enviar comentário"
          title="Enviar (Ctrl+Enter)"
          style={{ position: "absolute", right: 8, bottom: 8, width: 30, height: 30, minWidth: 0, padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: 8 }}
        >
          <FiSend size={14} style={{ display: "block" }} />
        </button>
        {candidatos.length ? (
          <div style={{ position: "absolute", left: 0, bottom: "100%", marginBottom: 4, background: "var(--bg-elevated)", border: "1px solid var(--border)", borderRadius: 10, boxShadow: "var(--shadow-3)", zIndex: 5, minWidth: 220 }}>
            {candidatos.map((p) => (
              <button key={p.id} type="button" onClick={() => mencionar(p)} style={{ display: "flex", gap: 8, alignItems: "center", width: "100%", padding: "6px 10px", background: "none", border: "none", color: "var(--ink)", font: "inherit", fontSize: 13, cursor: "pointer", textAlign: "left" }}>
                <Avatar nome={p.nome} size={22} /> {p.nome}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- corpo

export function DetalheCorpo({ req, onRecarregar, onSituacaoItem, onGerarOc, podeGerir, pessoas = [] }) {
  const { request, isMaster, hasAllPermissions } = useAuth();
  const podeConsolidar = isMaster || hasAllPermissions(["compras:consolidar_requisicao"]);
  const [eventos, setEventos] = useState([]);
  const [comentarios, setComentarios] = useState(req?.comentarios || []);
  const [editandoComprador, setEditandoComprador] = useState(false);
  const [itemCotando, setItemCotando] = useState(null);
  const [selecionados, setSelecionados] = useState(() => new Set());
  // { itens, fornecedor } da cotacao conjunta aberta; "vencedor" = modal do lote.
  const [cotando, setCotando] = useState(null);
  const [vencedorAberto, setVencedorAberto] = useState(false);

  const cotaveis = useMemo(
    () => (podeGerir ? (req.itens || []).filter(itemCotavel) : []),
    [req.itens, podeGerir],
  );
  // Item que deixou de ser cotavel (comprado, cancelado, devolvido na
  // junção desfeita) sai da seleção sozinho.
  useEffect(() => {
    const vivos = new Set(cotaveis.map((i) => i.id));
    setSelecionados((sel) => {
      const filtrado = new Set([...sel].filter((id) => vivos.has(id)));
      return filtrado.size === sel.size ? sel : filtrado;
    });
  }, [cotaveis]);
  useEffect(() => { setSelecionados(new Set()); }, [req.id]);
  const itensSelecionados = cotaveis.filter((i) => selecionados.has(i.id));
  const alternar = (id) => setSelecionados((sel) => {
    const novo = new Set(sel);
    if (novo.has(id)) novo.delete(id); else novo.add(id);
    return novo;
  });
  const abrirCotacao = (itens, fornecedor = "") => setCotando({ itens, fornecedor });
  async function depoisDeCotar() {
    setCotando(null);
    setVencedorAberto(false);
    await onRecarregar();
  }

  useEffect(() => { setComentarios(req?.comentarios || []); }, [req?.comentarios]);
  useEffect(() => {
    if (!req?.id) return;
    eventosDaRequisicao(request, req.id).then((r) => setEventos(r?.eventos || [])).catch(() => setEventos([]));
  }, [request, req?.id, req?.updated_at]);

  const situacao = req.situacao;
  const idxAtual = STEPPER.findIndex((s) => s.value === situacao);
  // Aguardando entrega entra: um item pode precisar voltar de etapa com outro ja em OC.
  const podeMudarItem = podeGerir && ["RASCUNHO", "EM_COTACAO", "APROVADA", "AGUARDANDO_ENTREGA"].includes(situacao);
  const valorCotado = useMemo(() => {
    let total = 0; let n = 0;
    (req.itens || []).forEach((it) => {
      const v = (it.cotacoes || []).find((c) => c.vencedora);
      if (v?.total_com_frete) { total += Number(v.total_com_frete); n += 1; }
    });
    return { total, n };
  }, [req.itens]);

  async function recarregarComentarios() {
    const r = await listarComentarios(request, req.id);
    setComentarios(r?.comentarios || []);
  }

  async function mudarComprador(id) {
    await atribuirComprador(request, req.id, id || null);
    setEditandoComprador(false);
    await onRecarregar();
  }

  // As datas só mudam enquanto a requisição está aberta (o backend também recusa).
  const podeEditarDatas = podeGerir && !["ATENDIDA", "CANCELADA", "CONSOLIDADA"].includes(situacao);
  async function mudarData(campo, valor) {
    await atualizarRequisicao(request, req.id, { [campo]: valor });
    await onRecarregar();
  }

  // "Aprovado" nao e clicavel (vem da aprovacao da requisicao); so aparece
  // no controle do item que ja esta aprovado, para mostrar onde ele esta.
  const opcoesItemDe = (it) => SITUACOES_ITEM_REQ
    .filter((o) => o.value !== "APROVADO" || it.situacao === "APROVADO")
    .map((o) => ({ ...o, tone: o.tone === "neutral" ? "accent" : o.tone }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      <Stepper aria-label="Etapas">
        {STEPPER.map((s, i) => {
          const on = i <= idxAtual && situacao !== "CANCELADA";
          const cor = getToneTokens(null, SITUACAO_TONE[s.value]).accent;
          return (
            <div key={s.value} data-on={i === idxAtual ? "true" : "false"}>
              <div className="barra" style={{ background: on ? cor : undefined }} />
              <div className="rotulo">{s.curto}</div>
            </div>
          );
        })}
      </Stepper>

      <MetaGrid>
        <div>
          <Rotulo>Prazo de cotação</Rotulo>
          <div className="v" style={{ color: req.atrasada ? "var(--danger-text)" : undefined }}>
            <DataEditavel
              valor={req.prazo_cotacao}
              podeEditar={podeEditarDatas}
              onSalvar={(v) => mudarData("prazo_cotacao", v)}
              rotuloDoBotao="Alterar prazo de cotação"
            />
            {req.atrasada ? <span style={{ fontWeight: 400 }}>· atrasada {-req.dias_para_prazo} dia{-req.dias_para_prazo === 1 ? "" : "s"}</span> : null}
          </div>
        </div>
        <div>
          <Rotulo>Comprador</Rotulo>
          {editandoComprador ? (
            <select autoFocus defaultValue={req.comprador || ""} onChange={(e) => mudarComprador(e.target.value)} onBlur={() => setEditandoComprador(false)} style={CAMPO}>
              <option value="">— Sem responsável —</option>
              {pessoas.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
            </select>
          ) : (
            <div className="v">
              <Avatar nome={req.comprador_nome} size={24} /> {req.comprador_nome || "—"}
              {podeGerir ? <button type="button" onClick={() => setEditandoComprador(true)} style={{ background: "none", border: "none", color: "var(--accent)", fontSize: 12, cursor: "pointer", padding: 0 }}>alterar</button> : null}
            </div>
          )}
        </div>
        {req.origem === "PHARUCUT" ? (
          <div>
            <Rotulo>Serra precisa em</Rotulo>
            <div className="v">
              <DataEditavel
                valor={req.data_necessaria}
                podeEditar={podeEditarDatas}
                onSalvar={(v) => mudarData("data_necessaria", v)}
                rotuloDoBotao="Alterar a data em que a serra precisa do material"
                vazio="sem data"
              />
              {req.substitui_numero ? (
                <span style={{ fontWeight: 400, color: "var(--ink-4)", fontSize: 12 }}>
                  {" "}· substitui{" "}
                  <Link to={`/suprimentos/requisicoes/${req.substitui}`}>{req.substitui_numero}</Link>
                </span>
              ) : null}
            </div>
          </div>
        ) : null}
        <div>
          <Rotulo>Parada há</Rotulo>
          <div className="v">{paradaHa(req) || "—"}</div>
        </div>
        <div>
          <Rotulo>Solicitada por</Rotulo>
          <div className="v">{req.solicitado_por_nome || "—"} <span style={{ color: "var(--ink-4)", fontWeight: 400 }}>· {dataHora(req.created_at)}</span></div>
        </div>
        <div>
          <Rotulo>Origem</Rotulo>
          <div className="v">
            <TagOrigem origem={req.origem} label={req.origem_label} />
            {req.cut_batch_id ? (
              <a href={`${PHARUCUT_URL}/batches/${req.cut_batch_id}`} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>
                Abrir lote <FiExternalLink size={11} />
              </a>
            ) : null}
            {req.lista_compras ? <Link to={`/revisor-ferragens/listas/${req.lista_compras}`} style={{ fontSize: 12 }}>Lista de origem</Link> : null}
          </div>
          {(req.consolidadas || []).length ? (
            <div style={{ fontSize: 11, color: "var(--ink-4)", marginTop: 2 }}>
              consolidou{" "}
              {req.consolidadas.map((c, i) => (
                <React.Fragment key={c.id}>
                  {i ? ", " : ""}
                  <Link to={`/suprimentos/requisicoes/${c.id}`} style={{ color: "inherit" }}>{c.numero}</Link>
                </React.Fragment>
              ))}
            </div>
          ) : null}
        </div>
        <div>
          <Rotulo>Valor cotado</Rotulo>
          <div className="v">
            <Mono $size={13}>{valorCotado.n ? formatarReaisBR(valorCotado.total) : "—"}</Mono>
            <span style={{ color: "var(--ink-4)", fontWeight: 400, fontSize: 12 }}>
              {valorCotado.n ? `${valorCotado.n} de ${(req.itens || []).length} itens` : "sem vencedor"}
              {Number(req.valor_em_aberto || 0) > 0 ? ` · OC ${formatarReaisBR(req.valor_em_aberto)}` : ""}
            </span>
          </div>
        </div>
      </MetaGrid>

      {req.situacao === "CONSOLIDADA" && req.consolidada_na ? (
        <Caixa>
          <strong>Consolidada</strong> — os itens foram juntados em{" "}
          <Link to={`/suprimentos/requisicoes/${req.consolidada_na}`}>{req.consolidada_na_numero}</Link>
          {req.consolidada_em ? ` em ${dataHora(req.consolidada_em)}` : ""}.
        </Caixa>
      ) : null}
      {req.complementar ? (
        <Caixa style={{ background: "var(--warning-bg)", color: "var(--warning-text)" }}>
          <strong>Chapa em falta no lote</strong> — pedido feito pelo gestor no PharuCut com o lote já liberado ou em
          corte. A quantidade é a que ele pediu: não é recalculada pelo saldo do estoque, porque a chapa que faltou já
          tinha sido baixada para o lote. Enquanto este pedido não for atendido, a chapa fica travada no Terminal.
          {req.requisicao_do_plano ? (
            <>
              {" "}Requisição do plano deste lote:{" "}
              <Link to={`/suprimentos/requisicoes/${req.requisicao_do_plano}`} style={{ color: "inherit" }}>
                {req.requisicao_do_plano_numero}
              </Link>
              .
            </>
          ) : null}
        </Caixa>
      ) : null}
      {/* Requisição do PLANO do PharuCut: a justificativa era sempre a mesma frase
          ("Consumo do plano aceito do lote…") — sai, e entra o que o comprador
          precisa: os ambientes do lote ("substitui" já está no cabeçalho acima).
          O pedido de chapa (complementar) mantém a dele: é o motivo do gestor. */}
      {req.origem === "PHARUCUT" && !req.complementar ? (
        req.ambientes_do_lote ? (
          <div style={{ fontSize: 13 }} title={req.ambientes_do_lote}>
            <span style={{ color: "var(--ink-4)" }}>Ambientes:</span> {req.ambientes_do_lote}
          </div>
        ) : null
      ) : req.justificativa ? (
        <Caixa style={{ whiteSpace: "pre-line" }}>
          <strong>Justificativa</strong> — {req.justificativa}
        </Caixa>
      ) : null}
      {req.incompleta ? (
        <Caixa style={{ background: "var(--warning-bg)", color: "var(--warning-text)" }}>
          <strong>Incompleta</strong> — o plano consome material que ainda não tem cadastro no ERP; a compra dele não
          está aqui e a requisição não pode ser dada por recebida. Cadastre com o mesmo código e ela se completa sozinha:{" "}
          {(req.linhas_cut || []).filter((l) => l.situacao === "SEM_CADASTRO" || l.situacao === "NAO_CONVERTE").map((l, i) => (
            <React.Fragment key={l.id}>
              {i ? ", " : ""}
              <Mono $size={12}>{l.codigo}</Mono>
              {l.motivo && l.situacao === "NAO_CONVERTE" ? ` (${l.motivo})` : ""}
            </React.Fragment>
          ))}
          {" "}· <Link to="/suprimentos/produtos" style={{ color: "inherit" }}>Cadastrar material</Link>
        </Caixa>
      ) : null}
      {req.origem === "PHARUCUT" && (req.linhas_cut || []).length ? <PlanoDoCut linhas={req.linhas_cut} /> : null}
      {req.motivo_cancelamento ? <Caixa style={{ background: "var(--danger-bg)", color: "var(--danger-text)" }}><strong>Cancelada</strong> — {req.motivo_cancelamento}</Caixa> : null}

      <BarraDeSelecao
        cotaveis={cotaveis}
        selecionados={itensSelecionados}
        onTodos={(marcar) => setSelecionados(marcar ? new Set(cotaveis.map((i) => i.id)) : new Set())}
        onCotar={() => abrirCotacao(itensSelecionados)}
        onVencedor={() => setVencedorAberto(true)}
        onLimpar={() => setSelecionados(new Set())}
      />

      <ItensDaRequisicao
        req={req}
        podeGerir={podeGerir}
        podeConsolidar={podeConsolidar}
        onRecarregar={onRecarregar}
        renderLinha={(it, extras) => (
            <div className="item" key={it.id}>
              {podeGerir ? (
                <input
                  type="checkbox"
                  checked={selecionados.has(it.id)}
                  disabled={!itemCotavel(it)}
                  onChange={() => alternar(it.id)}
                  aria-label={`Selecionar ${nomeDoItem(it)} para cotar junto`}
                  title={itemCotavel(it) ? "Selecionar para cotar junto" : "Item comprado ou cancelado"}
                  style={{ width: 16, height: 16, flex: "0 0 auto", cursor: itemCotavel(it) ? "pointer" : "not-allowed" }}
                />
              ) : null}
              <div style={{ flex: "1 1 220px", minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{nomeDoItem(it)}</div>
                <div style={{ fontSize: 12, color: "var(--ink-3)" }}>
                  {detalheDoItem(it)}
                  {it.observacao ? ` · ${it.observacao}` : ""}
                </div>
                {it.especificacao ? <div style={{ fontSize: 12, color: "var(--ink-3)" }}>{it.especificacao}</div> : null}
                <PedidosDoItem pedidos={it.pedidos} />
                {it.eh_terceiro && !(it.anexos || []).length ? (
                  <div style={{ fontSize: 12, color: "var(--warning-text)", display: "inline-flex", gap: 5, alignItems: "center", marginTop: 2 }}>
                    <FiPaperclip size={12} /> Sem detalhamento — pedir ao projetista
                  </div>
                ) : null}
                {extras}
                {it.ordem_compra_numero ? (
                  <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 2 }}>
                    OC <Mono $size={11}>{it.ordem_compra_numero}</Mono>{it.ordem_compra_fornecedor ? ` · ${it.ordem_compra_fornecedor}` : ""}
                    {it.oc_qtd ? ` · recebido ${fmtQt(it.oc_qtd_recebida || 0)}/${fmtQt(it.oc_qtd)}` : ""}
                  </div>
                ) : it.comprado_por_nome ? (
                  <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 2 }}>{it.observacao_compra} · {it.comprado_por_nome}</div>
                ) : null}
              </div>
              <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <Mono $size={13}>×{fmtQt(it.qtd_aprovada || it.qtd_solicitada)}</Mono>
                {podeMudarItem ? (
                  <Segmented
                    ariaLabel={`Situação de ${nomeDoItem(it)}`}
                    opcoes={opcoesItemDe(it)}
                    valor={it.situacao || "PENDENTE"}
                    disabled={Boolean(it.ordem_compra_numero)}
                    onChange={(v) => onSituacaoItem(it, v)}
                  />
                ) : (
                  <Tag $tone={SITUACOES_ITEM_REQ.find((o) => o.value === it.situacao)?.tone || "neutral"}>{SITUACAO_ITEM_LABEL[it.situacao] || it.situacao}</Tag>
                )}
                {(it.cotacoes || []).length || (podeGerir && !["COMPRADO", "CANCELADO"].includes(it.situacao)) ? (
                  <button type="button" className="btn btn--secondary btn--sm" onClick={() => ((it.cotacoes || []).length
                    ? setItemCotando((c) => (c === it.id ? null : it.id))
                    : abrirCotacao([it]))}>
                    <FiDollarSign size={12} /> {(it.cotacoes || []).length ? `${it.cotacoes.length} ${it.cotacoes.length === 1 ? "cotação" : "cotações"}` : "Cotar"}
                  </button>
                ) : null}
              </div>
              {itemCotando === it.id ? (
                <div style={{ flexBasis: "100%", paddingTop: 4 }}>
                  <CotacaoDoItem req={req} item={it} podeGerir={podeGerir} onMudou={onRecarregar} onGerarOc={onGerarOc} onCotar={abrirCotacao} />
                </div>
              ) : null}
            </div>
        )}
      />

      <QuadroFornecedores totais={req.totais_cotacao} />

      {cotando ? (
        <CotacaoConjunta
          req={req}
          itens={cotando.itens}
          fornecedorInicial={cotando.fornecedor}
          onFechar={() => setCotando(null)}
          onSalvo={depoisDeCotar}
        />
      ) : null}
      {vencedorAberto ? (
        <VencedorEmLote req={req} itens={itensSelecionados} onFechar={() => setVencedorAberto(false)} onSalvo={depoisDeCotar} />
      ) : null}

      <RqcsDaRequisicao req={req} rqcs={req.rqcs} />

      <Duas>
        <div>
          <Rotulo>Histórico</Rotulo>
          {eventos.length === 0 ? (
            <div style={{ fontSize: 12, color: "var(--ink-4)" }}>Sem eventos.</div>
          ) : (
            <Trilho>
              {eventos.slice(0, 30).map((e) => (
                <div key={e.id} style={{ "--dot": getToneTokens(null, TIPO_EVENTO_TONE[e.tipo] || "neutral").accent }}>
                  <span className="q">{dataHora(e.timestamp)}</span> <strong>{e.ator_nome || "Sistema"}</strong> {e.detalhe}
                </div>
              ))}
            </Trilho>
          )}
        </div>
        <Comentarios req={req} comentarios={comentarios} onEnviado={recarregarComentarios} pessoas={pessoas} />
      </Duas>
    </div>
  );
}

// --------------------------------------------------------------- drawer

/**
 * Drawer da fila. `onMover(req, destino, extras)` resolve qualquer ação de
 * situação (aprovar, receber, cancelar, reabrir…) pelo backend.
 */
export default function Detalhe({
  open, req, loading, onClose, onMover, onSituacaoItem, onRecarregar, onGerarOc, podeGerir, podeAprovar, pessoas,
}) {
  const [modal, setModal] = useState(null); // {destino, precisa}
  const situacao = req?.situacao;
  const terminal = situacao === "ATENDIDA" || situacao === "CANCELADA" || situacao === "CONSOLIDADA";
  // Consolidada nao reabre: os itens ja vivem na principal.
  const reabrivel = situacao === "ATENDIDA" || situacao === "CANCELADA";

  useEffect(() => { if (!open) setModal(null); }, [open]);

  function pedir(destino, precisa) {
    if (!precisa) { onMover(req, destino, {}); return; }
    setModal({ destino, precisa });
  }

  const titulo = req ? (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
      <Mono $size={18} style={{ fontWeight: 500 }}>{req.numero}</Mono>
      <StatusPill situacao={situacao} />
      <TagOrigem origem={req.origem} label={req.origem_label} />
      {req.prioridade === "URGENTE" ? <TagUrgente /> : null}
    </span>
  ) : "Requisição";

  const rodape = req ? (
    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
      {podeGerir && !terminal ? (
        <button type="button" className="btn btn--secondary" style={{ color: "var(--danger-text)" }} onClick={() => pedir("CANCELADA", "motivo")}>
          Cancelar requisição
        </button>
      ) : null}
      {podeGerir && reabrivel ? (
        <button type="button" className="btn btn--secondary" onClick={() => pedir("APROVADA", "motivo")}>Reabrir como aprovada</button>
      ) : null}
      <span style={{ flex: 1 }} />
      {podeGerir && situacao === "RASCUNHO" ? (
        <button type="button" className="btn btn--secondary" onClick={() => pedir("EM_COTACAO", "")}>Marcar em andamento</button>
      ) : null}
      {podeGerir && (situacao === "EM_COTACAO" || situacao === "APROVADA") ? (
        <button type="button" className="btn btn--secondary" onClick={() => pedir("RASCUNHO", "motivo")}>Voltar para "A cotar"</button>
      ) : null}
      {podeAprovar && (situacao === "RASCUNHO" || situacao === "EM_COTACAO") ? (
        <button type="button" className="btn btn--primary" onClick={() => pedir("APROVADA", "")}><FiCheck size={14} /> Aprovar</button>
      ) : null}
      {podeGerir && situacao === "APROVADA" ? (
        <button type="button" className="btn btn--primary" onClick={() => onGerarOc?.(null)}><FiDollarSign size={14} /> Gerar OC</button>
      ) : null}
      {podeAprovar && situacao === "APROVADA" ? (
        <button type="button" className="btn btn--secondary" onClick={() => pedir("ATENDIDA", "observacao")}>Dar por recebida (sem OC)</button>
      ) : null}
      {podeAprovar && situacao === "AGUARDANDO_ENTREGA" ? (
        <button type="button" className="btn btn--primary" onClick={() => pedir("ATENDIDA", "observacao")}><FiCheck size={14} /> Confirmar recebimento</button>
      ) : null}
    </div>
  ) : null;

  const TEXTOS = {
    CANCELADA: { titulo: "Cancelar requisição", descricao: "Os itens abertos serão cancelados. Informe o motivo.", tone: "danger", confirmarLabel: "Cancelar requisição" },
    ATENDIDA: { titulo: situacao === "AGUARDANDO_ENTREGA" ? "Confirmar recebimento" : "Dar por recebida sem OC", descricao: situacao === "AGUARDANDO_ENTREGA" ? "Registra a entrada do saldo pendente de cada OC no estoque." : "A requisição fecha sem fornecedor nem valor; use Gerar OC para registrar a compra.", rotulo: "Observação", obrigatorio: false, confirmarLabel: "Confirmar" },
    RASCUNHO: { titulo: "Voltar para 'A cotar'", descricao: "Os itens fechados por fora voltam a ficar pendentes.", confirmarLabel: "Voltar" },
    APROVADA: { titulo: "Reabrir como aprovada", descricao: "A requisição volta à fila do comprador.", confirmarLabel: "Reabrir" },
  };

  return (
    <>
      <SlideOverPanel
        open={open}
        onClose={onClose}
        title={titulo}
        subtitle={req ? `${req.cliente_nome || "Sem cliente"}${req.projeto_resumo ? ` · ${req.projeto_resumo}` : ""}` : ""}
        width="min(94vw, 760px)"
        footer={rodape}
      >
        {loading || !req ? (
          <Carregando linhas={3} colunas={2} />
        ) : (
          <>
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
              <Link to={`/suprimentos/requisicoes/${req.id}`} className="btn btn--secondary btn--sm" onClick={onClose}>
                Abrir página <FiExternalLink size={12} />
              </Link>
            </div>
            <DetalheCorpo
              req={req}
              onRecarregar={onRecarregar}
              onSituacaoItem={onSituacaoItem}
              onGerarOc={onGerarOc}
              podeGerir={podeGerir}
              pessoas={pessoas}
            />
          </>
        )}
      </SlideOverPanel>
      <ModalMotivo
        open={Boolean(modal)}
        {...(modal ? TEXTOS[modal.destino] || { titulo: "Confirmar" } : {})}
        onCancelar={() => setModal(null)}
        onConfirmar={async (texto) => {
          const extras = modal.precisa === "observacao" ? { observacao: texto } : { motivo: texto };
          const ok = await onMover(req, modal.destino, extras);
          if (ok !== false) setModal(null);
        }}
      />
    </>
  );
}
